import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Loader2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

import FunnelChart, { STAGE_META } from "@/components/performance/FunnelChart";
import DrillDownPanel from "@/components/performance/DrillDownPanel";
import AlertsWidget from "@/components/AlertsWidget";
import { PERIODS, fmtNumber } from "@/components/performance/PerfSharedUI";
import CustomDateRange from "@/components/performance/CustomDateRange";
import FiltersBar from "@/components/performance/FiltersBar";
import SlaCard from "@/components/performance/SlaCard";
import GlobalConversionCard from "@/components/performance/GlobalConversionCard";
import ConversionsGrid from "@/components/performance/ConversionsGrid";
import StageCard from "@/components/performance/StageCard";
import PerOfficeTable from "@/components/performance/PerOfficeTable";
import PerOperatorTable from "@/components/performance/PerOperatorTable";

// ---- Static fallbacks --------------------------------------------------
const SOURCE_HINTS = [
  "site", "google", "meta", "facebook", "instagram",
  "referencia", "parceiro", "orgânico", "telefone",
];

// ---- Small summary cards used only on this page ------------------------
function VolumeCard({ counts }) {
  return (
    <div className="rounded-2xl border-2 border-border bg-card p-6" data-testid="volume-summary">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] font-semibold">Volume</div>
      <h3 className="font-display text-xl">Este período</h3>
      <div className="mt-3 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-[#6B7280]">Leads recebidos</span>
          <strong>{fmtNumber(counts?.leads_received)}</strong>
        </div>
        <div className="flex justify-between">
          <span className="text-[#6B7280]">Estimativas enviadas</span>
          <strong>{fmtNumber(counts?.estimates_sent)}</strong>
        </div>
        <div className="flex justify-between">
          <span className="text-[#6B7280]">Negócios fechados</span>
          <strong className="text-[hsl(var(--success))]">{fmtNumber(counts?.deals_closed)}</strong>
        </div>
      </div>
    </div>
  );
}

function PrevPeriodCard({ prev, period }) {
  return (
    <div className="rounded-2xl border-2 border-border bg-card p-6" data-testid="prev-summary">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] font-semibold">Período anterior</div>
      <h3 className="font-display text-xl">Comparação</h3>
      {prev ? (
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-[#6B7280]">Leads</span>
            <strong>{fmtNumber(prev.counts?.leads_received)}</strong>
          </div>
          <div className="flex justify-between">
            <span className="text-[#6B7280]">Estimativas</span>
            <strong>{fmtNumber(prev.counts?.estimates_sent)}</strong>
          </div>
          <div className="flex justify-between">
            <span className="text-[#6B7280]">Negócios</span>
            <strong>{fmtNumber(prev.counts?.deals_closed)}</strong>
          </div>
        </div>
      ) : (
        <div className="mt-3 text-xs text-[#6B7280]">
          Sem período comparável para "{PERIODS.find((p) => p.k === period)?.t || period}".
        </div>
      )}
    </div>
  );
}

// ---- PAGE --------------------------------------------------------------
export default function PerformanceDashboard() {
  const [period, setPeriod] = useState("today");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [filters, setFilters] = useState({
    office: "", operator_id: "", source: "", service_type: "", status: "",
    include_lisboa: false,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [funnelMode, setFunnelMode] = useState("global"); // "global" | "qualified"
  const [drill, setDrill] = useState({ open: false, stage: null, invert: false });
  const [tab, setTab] = useState("global"); // "global" | "office" | "operator"
  const currentReqKeyRef = useRef("");

  const loadPerformance = async () => {
    setLoading(true);
    // Guard against stale responses: capture the current request's key and only
    // apply setData if it still matches by the time the response arrives.
    const requestKey = `${period}|${customRange.start}|${customRange.end}|${JSON.stringify(filters)}`;
    currentReqKeyRef.current = requestKey;
    try {
      const params = { period, ...filters };
      if (period === "custom") { params.start = customRange.start; params.end = customRange.end; }
      Object.keys(params).forEach((k) => {
        if (params[k] === "" || params[k] === false) delete params[k];
      });
      if (filters.include_lisboa) params.include_lisboa = true;
      const r = await api.get("/performance/summary", { params });
      if (currentReqKeyRef.current === requestKey) setData(r.data);
    } catch (e) {
      if (currentReqKeyRef.current === requestKey) {
        toast.error(e.response?.data?.detail || "Erro ao carregar performance");
      }
    } finally {
      if (currentReqKeyRef.current === requestKey) setLoading(false);
    }
  };

  useEffect(() => { loadPerformance(); }, [period, customRange, JSON.stringify(filters)]);  // eslint-disable-line
  useEffect(() => {
    if (typeof window !== "undefined") window.__PERF_ROUTE_READY__ = true;
  }, []);

  const availableOffices = useMemo(() => (
    data?.per_office
      ? data.per_office.map((o) => o.office).filter((o) => o && o !== "—")
      : []
  ), [data]);
  const availableOperators = useMemo(() => (
    data?.per_operator
      ? data.per_operator.map((o) => ({ operator_id: o.operator_id, operator_name: o.operator_name }))
      : []
  ), [data]);

  const openDrill = (stage, invert = false) => setDrill({ open: true, stage, invert });
  const closeDrill = () => setDrill({ open: false, stage: null, invert: false });

  const funnelData = useMemo(() => {
    if (!data) return null;
    if (funnelMode === "global") return data;
    // Qualified funnel: recompute using leads_qualified as new "top of funnel"
    const c = { ...data.counts };
    const q = c.leads_qualified || 0;
    return {
      ...data,
      conversions: {
        lead_to_contact: 100, contact_to_qualified: 100,
        qualified_to_estimate: q ? Math.min(100, (c.estimates_sent / q) * 100) : 0,
        estimate_to_rt: data.conversions.estimate_to_rt,
        rt_scheduled_to_realised: data.conversions.rt_scheduled_to_realised,
        rt_to_proposal: data.conversions.rt_to_proposal,
        proposal_to_deal: data.conversions.proposal_to_deal,
        lead_to_deal: q ? Math.min(100, (c.deals_closed / q) * 100) : 0,
      },
    };
  }, [data, funnelMode]);

  const hideStages = funnelMode === "qualified"
    ? ["leads_received", "leads_contacted"] : [];

  return (
    <div className="p-8 lg:p-12 max-w-[1600px] mx-auto space-y-6" data-testid="performance-root">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <Link to="/dashboard"
                className="inline-flex items-center gap-1 text-xs text-[#6B7280] hover:text-foreground mb-2"
                data-testid="back-dashboard">
            <ArrowLeft className="w-3 h-3" /> Dashboard
          </Link>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-1">
            Painel de gestão
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">
            Performance do Atendimento
          </h1>
          <p className="text-sm text-[#6B7280] mt-2 max-w-2xl">
            Funil do Lead ao Negócio, SLA de 15 minutos e progresso por escritório e operador — em tempo real.
          </p>
        </div>
        <button
          onClick={loadPerformance}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm hover:bg-muted/40 disabled:opacity-50 flex-shrink-0"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      {/* Period selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {PERIODS.map((p) => (
          <button
            key={p.k}
            onClick={() => setPeriod(p.k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${period === p.k ? "bg-foreground text-background" : "bg-card border border-border text-foreground hover:bg-muted/40"}`}
            data-testid={`period-${p.k}`}
          >
            {p.t}
          </button>
        ))}
        <button
          onClick={() => setPeriod("custom")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${period === "custom" ? "bg-foreground text-background" : "bg-card border border-border text-foreground hover:bg-muted/40"}`}
          data-testid="period-custom"
        >
          Intervalo…
        </button>
        {period === "custom" && (
          <CustomDateRange
            start={customRange.start}
            end={customRange.end}
            onChange={setCustomRange}
          />
        )}
      </div>

      {/* Filters */}
      <FiltersBar
        filters={filters}
        setFilters={setFilters}
        availableOffices={availableOffices}
        availableOperators={availableOperators}
        availableSources={SOURCE_HINTS}
      />

      {loading && (
        <div className="text-sm text-[#6B7280] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar dados…
        </div>
      )}

      {!loading && data && (
        <>
          {/* Top row: SLA + Global + Volume + Prev */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <SlaCard data={data} />
            <GlobalConversionCard data={data} />
            <VolumeCard counts={data.counts} />
            <PrevPeriodCard prev={data.prev} period={period} />
          </div>

          {/* Alerts widget + Stage cards row */}
          <AlertsWidget />
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            {STAGE_META.map((stage) => (
              <StageCard
                key={stage.key}
                stageKey={stage.key}
                count={data.counts?.[stage.key]}
                delta={data.deltas?.[stage.key]}
                classification={stage.conv ? data.classifications?.[stage.conv] : "target"}
                extraLabel={stage.conv ? "conv" : ""}
                extraValue={stage.conv ? `${data.conversions?.[stage.conv]?.toFixed(0)}%` : ""}
                onClick={() => openDrill(stage.key)}
                testid={`stage-${stage.key}`}
              />
            ))}
          </div>

          {/* Funnel mode toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6B7280] font-semibold uppercase tracking-[0.15em]">
              Ver funil:
            </span>
            <button
              onClick={() => setFunnelMode("global")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${funnelMode === "global" ? "bg-foreground text-background" : "bg-card border border-border text-foreground"}`}
              data-testid="funnel-mode-global"
            >
              Global
            </button>
            <button
              onClick={() => setFunnelMode("qualified")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${funnelMode === "qualified" ? "bg-foreground text-background" : "bg-card border border-border text-foreground"}`}
              data-testid="funnel-mode-qualified"
            >
              Só qualificados
            </button>
          </div>

          <FunnelChart
            data={funnelData}
            onStageClick={(s) => openDrill(s, false)}
            onLossClick={(s) => openDrill(s, true)}
            hideStages={hideStages}
          />

          <ConversionsGrid data={data} />

          {/* Tabs */}
          <div className="flex items-center gap-2 border-b border-border">
            {[
              { k: "global", t: "Global" },
              { k: "office", t: "Por escritório" },
              { k: "operator", t: "Por operador" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-4 py-2 -mb-px text-sm font-semibold border-b-2 transition-all ${tab === t.k ? "border-[#0F172A] text-foreground" : "border-transparent text-[#6B7280] hover:text-foreground"}`}
                data-testid={`tab-${t.k}`}
              >
                {t.t}
              </button>
            ))}
          </div>

          {tab === "office" && (
            <PerOfficeTable
              rows={data.per_office}
              onSelect={(office) => setFilters((f) => ({ ...f, office }))}
            />
          )}
          {tab === "operator" && <PerOperatorTable rows={data.per_operator} />}
          {tab === "global" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PerOfficeTable
                rows={data.per_office?.slice(0, 5)}
                onSelect={(office) => setFilters((f) => ({ ...f, office }))}
              />
              <PerOperatorTable rows={data.per_operator?.slice(0, 5)} />
            </div>
          )}
        </>
      )}

      <DrillDownPanel
        open={drill.open}
        onClose={closeDrill}
        stage={drill.stage}
        invert={drill.invert}
        filters={{
          period, start: customRange.start, end: customRange.end,
          office: filters.office, operator_id: filters.operator_id,
          source: filters.source, service_type: filters.service_type,
          status: filters.status, include_lisboa: filters.include_lisboa,
        }}
      />
    </div>
  );
}
